import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter, Navigate } from 'react-router';
import { AuthProvider } from 'react-oidc-context';
import { AuthCallback } from './components/RequireAuth';
import { Layout } from './components/Layout';
import { WelcomePage } from './pages/WelcomePage';
import { SnippetsPage } from './pages/SnippetsPage';
import { QueryPage } from './pages/QueryPage';
import { BrowsePage } from './pages/BrowsePage';
import { VizPage } from './pages/VizPage';
import { VocabularyPage } from './pages/VocabularyPage';
import { OntologyEditorPage } from './pages/OntologyEditorPage';
import { CatalogPage } from './pages/CatalogPage';
import { EntityInfoPage } from './pages/EntityInfoPage';
import { RulesPage } from './pages/RulesPage';
import { DocsPage } from './pages/DocsPage';
import { PublicTermsPage } from './pages/PublicTermsPage';
import { DashboardPage } from './pages/DashboardPage';
import { TimelinePage } from './pages/TimelinePage';
import { PreferencesPage } from './pages/PreferencesPage';
import { DatabaseManagementPage } from './pages/DatabaseManagementPage';
import { AdminPage } from './pages/AdminPage';
import { AiChatPage } from './pages/AiChatPage';
import { PageGuard } from './components/PageGuard';
import { loadAuthConfig } from './lib/auth-config';
import { applyTheme, getThemePreference, watchSystemTheme } from './lib/preferences';
import './index.css';

// Apply the persisted theme synchronously before the first render to
// avoid a flash of unstyled / light content. `darkMode: 'class'` in
// tailwind.config triggers off the `dark` class on <html>.
applyTheme(getThemePreference());
watchSystemTheme();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

// Load runtime config from /api/config before mounting the OIDC
// AuthProvider. Same SPA image, different customer → different
// Cognito values; the backend tells us which. See auth-config.ts.
let authConfig;
try {
  authConfig = await loadAuthConfig();
} catch (err) {
  console.error('Failed to load runtime config:', err);
  rootEl.innerHTML = `
    <div style="padding:2rem;font-family:system-ui;max-width:42rem;margin:auto;">
      <h1 style="font-size:1.25rem;font-weight:600;">Configuration unavailable</h1>
      <p style="margin-top:0.75rem;color:#555;">
        TReS could not load runtime configuration from <code>/api/config</code>.
        This usually means the backend API is unreachable or misconfigured.
        Please contact your administrator.
      </p>
      <pre style="background:#f5f5f5;padding:1rem;margin-top:1rem;overflow:auto;font-size:0.875rem;">${String(err).replace(/&/g, '&amp;').replace(/</g, '&lt;')}</pre>
    </div>
  `;
  throw err;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// `/auth/callback` is the OIDC return URL. AuthProvider's
// onSigninCallback strips ?code=&state=; AuthCallback then forwards to
// whatever destination RequireAuth recorded before the redirect.
const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    // Toggleable pages are wrapped in PageGuard so the admin Pages tab is
    // ACCESS control, not nav decoration — a disabled page redirects home
    // even by direct URL. Only welcome, preferences, docs, and admin are
    // unwrapped: the first two are always-on (ruled 2026-08-13), docs is
    // reference material, and /admin is gated by role rather than by the
    // page registry that it administers.
    children: [
      { index: true, element: <WelcomePage /> },
      { path: 'dashboard', element: <PageGuard page="dashboard"><DashboardPage /></PageGuard> },
      { path: 'timeline', element: <PageGuard page="timeline"><TimelinePage /></PageGuard> },
      { path: 'query', element: <PageGuard page="query"><QueryPage /></PageGuard> },
      { path: 'snippets', element: <PageGuard page="snippets"><SnippetsPage /></PageGuard> },
      { path: 'browse', element: <PageGuard page="browse"><BrowsePage /></PageGuard> },
      { path: 'viz', element: <PageGuard page="viz"><VizPage /></PageGuard> },
      { path: 'vocabulary', element: <PageGuard page="vocabulary"><VocabularyPage /></PageGuard> },
      { path: 'ontology', element: <PageGuard page="ontology"><OntologyEditorPage /></PageGuard> },
      { path: 'catalog', element: <PageGuard page="catalog"><CatalogPage /></PageGuard> },
      { path: 'entity', element: <PageGuard page="entity"><EntityInfoPage /></PageGuard> },
      { path: 'rules', element: <PageGuard page="rules"><RulesPage /></PageGuard> },
      { path: 'trainer', element: <PageGuard page="trainer"><AiChatPage /></PageGuard> },
      { path: 'preferences', element: <PreferencesPage /> },
      { path: 'docs', element: <DocsPage /> },
      { path: 'docs/:slug', element: <DocsPage /> },
      { path: 'admin/database', element: <PageGuard page="admin-load"><DatabaseManagementPage /></PageGuard> },
      { path: 'admin', element: <AdminPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
  // The OIDC return URL. Outside the authenticated Layout: RequireAuth wraps
  // Layout's Outlet, so a callback route nested there would be gated on the
  // very sign-in it is completing.
  { path: '/auth/callback', element: <AuthCallback /> },
  // Public, pre-auth Terms of Service — outside the authenticated Layout so the
  // invitation email can link to it before the invitee has signed in.
  { path: '/terms', element: <PublicTermsPage /> },
]);

createRoot(rootEl).render(
  <StrictMode>
    <AuthProvider {...authConfig.oidcProviderProps}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
);
